// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// net-game.ts — one Session drives a run, solo or peer-to-peer.
//
// ONE path, deliberately. Solo is simply "a Session whose net is undefined": the same code, unable
// to drift from the multiplayer one. A sibling game shipped broken precisely because its co-op shape
// got bespoke netcode and host transfer was never wired into it.
//
// ── authority ────────────────────────────────────────────────────────────────
// Host-authoritative star on a shared clock. The host runs the whole ship in `hostStep` and
// broadcasts a full snapshot at 12Hz. A client sends only ABSOLUTE, IDEMPOTENT commands and renders
// what it receives. Because a client rebuilds its entire Game from every snapshot, a promoted peer
// reconstructs NOTHING — `setHost(true)` re-points the hazard cursor and starts stepping. That is
// the whole takeover, and it is why the hazard schedule is public: a host-private director would
// destroy the crew's already-spoken forecast on every promotion.
//
// ── fairness ─────────────────────────────────────────────────────────────────
// The host buffers EVERY command including its own for INPUT_DELAY_TICKS and applies them ordered by
// the sender's claimed sim tick at press. Without that the host always takes load-window ordinal 1
// and is measurably playing an easier game than its guests.
//
// ── promotion is armed from SILENCE, not onPeerLeave ──────────────────────────
// `onPeerLeave` misses a suspended tab entirely, and 20s of grace is longer than this game's own
// time constants — a fire goes 88 to 100 in two seconds. So a client that has heard no snapshot for
// LOST_MS dims the ship and shows a horizon, and the survivors promote at PROMOTE_MS.

import { Game, type Act, type Ev, type LossCause } from './game';
import { modeOf } from './modes';
import { T } from './tuning';
import type { Net, PeerId } from '@ben-gy/game-engine/net';

/** Host -> all. The whole ship, ~1KB of JSON at 12Hz. */
interface Snap {
  k: number; // tick
  o: number; // output
  h: number; // heat x10
  u: number; // hull x10
  r: number; // transit x10
  d: number; // driftSecs x10
  sp: number[];
  pl: number;
  tu: number; // tripUntil
  su: number; // scramUntil
  fc: 0 | 1; // forcing
  ov: 0 | 1;
  wn: 0 | 1;
  cz: LossCause | null;
  /** per cell: fire x10, flood x10, breaches, warp, destroyed, charge x10, cutUntil */
  C: number[];
  /** per seat: actions, doused, sealed, cellSecs x10 */
  L: number[];
  /** last applied sequence per seat, so a client can clear its pending tint */
  q: number[];
  /** load-window action times x10, so a guest's ordinal readout is the host's */
  w: number[];
}

const LOST_MS = 1200;
const PROMOTE_MS = 3000;

export interface SessionCfg {
  game: Game;
  /** This peer's seat in the frozen roster, or -1 for a spectator. */
  me: number;
  ids: (PeerId | null)[];
  net?: Net;
  host?: boolean;
  onEnd: () => void;
  onHostChange?: (isHost: boolean) => void;
  onLink?: (state: 'ok' | 'lost' | 'promoting') => void;
}

export interface Session {
  pump(nowMs: number): void;
  /** Local player: take an action. Returns false if the rules refused it. */
  act(a: Omit<Act, 'sq'>): boolean;
  drainFx(): Ev[];
  setHost(isHost: boolean): void;
  onRoster(): void;
  isHost(): boolean;
  /** Sequence numbers still awaiting confirmation, for the pending tint. */
  pending(): number[];
  destroy(): void;
}

export function createSession(cfg: SessionCfg): Session {
  const { game: g, me, net } = cfg;
  let host = net ? !!cfg.host : true;
  let ended = false;
  let destroyed = false;

  const seatOf = new Map<PeerId, number>();
  cfg.ids.forEach((id, i) => {
    if (id) seatOf.set(id, i);
  });

  let last = 0;
  let acc = 0;
  let snapAcc = 0;
  let lastSnapMs = 0;
  let link: 'ok' | 'lost' | 'promoting' = 'ok';
  let seq = 0;
  const unconfirmed = new Set<number>();
  const fx: Ev[] = [];

  /** The host's ordering buffer. Every command waits, including the host's own. */
  let queue: { at: number; seat: number; act: Act }[] = [];

  const sendSnap = net?.channel<Snap>('snap', (s, from) => {
    if (host || from !== net.host()) return;
    lastSnapMs = nowMs();
    if (link !== 'ok') setLink('ok');
    applySnap(s);
  });

  const sendAct = net?.channel<Act & { st: number }>('act', (a, from) => {
    if (!host) return;
    const seat = seatOf.get(from);
    if (seat == null || seat < 0) return;
    enqueue(seat, a);
  });

  const sendEv = net?.channel<Ev[]>('evt', (list, from) => {
    if (host || from !== net.host()) return;
    for (const e of list) fx.push(e);
  });

  function nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : 0;
  }

  function setLink(s: typeof link): void {
    link = s;
    cfg.onLink?.(s);
  }

  function enqueue(seat: number, a: Act): void {
    // Clamp the claimed press tick into a sane window. A client cannot be trusted to order the whole
    // room by a number it made up, but it CAN be trusted about a tick it saw eight frames ago.
    const tk = Math.max(g.tick - 8, Math.min(g.tick, a.tk | 0));
    queue.push({ at: g.tick + T.INPUT_DELAY_TICKS, seat, act: { ...a, tk } });
  }

  function drainQueue(): void {
    if (!queue.length) return;
    const due = queue.filter((q) => q.at <= g.tick);
    if (!due.length) return;
    queue = queue.filter((q) => q.at > g.tick);
    due.sort((a, b) => a.act.tk - b.act.tk || a.seat - b.seat);
    for (const q of due) g.apply(q.seat, q.act);
  }

  function buildSnap(): Snap {
    const C: number[] = [];
    for (const c of g.cells) {
      C.push(
        Math.round(c.fire * 10),
        Math.round(c.flood * 10),
        c.breaches,
        c.warp,
        c.destroyed ? 1 : 0,
        Math.round(Math.max(0, c.charge) * 10),
        c.cutUntil,
        c.charge < 0 ? 1 : 0,
      );
    }
    const L: number[] = [];
    for (const s of g.seats) L.push(s.actions, s.doused, s.sealed, Math.round(s.cellSecs * 10));
    return {
      k: g.tick,
      o: g.output,
      h: Math.round(g.heat * 10),
      u: Math.round(g.hull * 10),
      r: Math.round(g.transit * 10),
      d: Math.round(g.driftSecs * 10),
      sp: g.spine.slice(),
      pl: g.poweredLen,
      tu: g.tripUntil,
      su: g.scramUntil,
      fc: g.forcing ? 1 : 0,
      ov: g.over,
      wn: g.win,
      cz: g.cause,
      C,
      L,
      q: g.lastSeq.slice(),
      w: g.windowActions.map((a) => Math.round(a.t * 10)),
    };
  }

  function applySnap(s: Snap): void {
    g.tick = s.k;
    g.t = s.k / T.TICK_HZ;
    g.output = s.o;
    g.heat = s.h / 10;
    g.hull = s.u / 10;
    g.transit = s.r / 10;
    g.driftSecs = s.d / 10;
    g.spine = s.sp.slice();
    g.poweredLen = s.pl;
    g.tripUntil = s.tu;
    g.scramUntil = s.su;
    g.forcing = s.fc === 1;
    g.win = s.wn;
    g.cause = s.cz;
    for (let i = 0; i < g.n && i * 8 + 7 < s.C.length; i++) {
      const c = g.cells[i];
      c.fire = s.C[i * 8] / 10;
      c.flood = s.C[i * 8 + 1] / 10;
      c.breaches = s.C[i * 8 + 2];
      c.warp = s.C[i * 8 + 3] as 0 | 1;
      c.destroyed = s.C[i * 8 + 4] === 1;
      c.charge = s.C[i * 8 + 7] === 1 ? -1 : s.C[i * 8 + 5] / 10;
      c.cutUntil = s.C[i * 8 + 6];
    }
    for (let i = 0; i < g.seats.length && i * 4 + 3 < s.L.length; i++) {
      g.seats[i].actions = s.L[i * 4];
      g.seats[i].doused = s.L[i * 4 + 1];
      g.seats[i].sealed = s.L[i * 4 + 2];
      g.seats[i].cellSecs = s.L[i * 4 + 3] / 10;
    }
    g.lastSeq = s.q.slice();
    g.windowActions = s.w.map((v) => ({ t: v / 10 }));
    // A command is confirmed once the host's applied-sequence reaches it. Absolute commands mean a
    // duplicate is a no-op, so a re-send can never double-charge the core.
    for (const n of [...unconfirmed]) if ((g.lastSeq[me] ?? -1) >= n) unconfirmed.delete(n);
    // rebuild the derived state the renderer reads
    g.recompute();
    g.poweredLen = s.pl;
    if (s.ov === 1) {
      g.over = 1;
      finish();
    }
  }

  function finish(): void {
    if (ended) return;
    ended = true;
    cfg.onEnd();
  }

  return {
    pump(ms) {
      if (destroyed) return;
      if (!last) {
        last = ms;
        lastSnapMs = ms;
        if (host && net) broadcast();
        return;
      }
      const dt = Math.min(0.25, (ms - last) / 1000);
      last = ms;

      if (host) {
        acc += dt;
        let steps = 0;
        // A FULL SECOND of catch-up. Eight steps at 30Hz is 0.27s, so a backgrounded host that
        // returns after a second would silently run the ship at a fraction of real speed.
        while (acc >= 1 / T.TICK_HZ && steps < T.CATCHUP_STEPS) {
          drainQueue();
          g.hostStep(1 / T.TICK_HZ);
          steps++;
          acc -= 1 / T.TICK_HZ;
        }
        if (steps >= T.CATCHUP_STEPS) acc = 0;

        if (g.events.length) {
          for (const e of g.events) fx.push(e);
          if (net) sendEv?.(g.events.slice(0, 40));
          g.events.length = 0;
        }

        snapAcc += dt;
        if (net && snapAcc >= 1 / T.SNAP_HZ) {
          snapAcc = 0;
          broadcast();
        }
        if (g.over) finish();
      } else if (net) {
        const silence = ms - lastSnapMs;
        if (silence > PROMOTE_MS && link !== 'promoting') setLink('promoting');
        else if (silence > LOST_MS && link === 'ok') setLink('lost');
      }
    },

    act(a) {
      if (me < 0 || g.over) return false;
      const full: Act = { ...a, sq: ++seq };
      if (host) {
        // The host queues its OWN commands too. Skipping the buffer here is exactly how a host ends
        // up permanently first in the load window.
        enqueue(me, full);
        return true;
      }
      unconfirmed.add(full.sq);
      sendAct?.({ ...full, st: g.tick });
      return true;
    },

    drainFx() {
      return fx.splice(0, fx.length);
    },

    setHost(isHost) {
      if (isHost === host) return;
      host = isHost;
      if (host) {
        // THE TAKEOVER. This peer already holds the whole ship from the last snapshot and the hazard
        // schedule is a pure function of (seed, mode, party), so there is nothing to rebuild but the
        // cursor into it.
        g.resumeHostState();
        acc = 0;
        snapAcc = 0;
        queue = [];
        ended = g.over === 1;
        setLink('ok');
        broadcast();
      }
      cfg.onHostChange?.(host);
    },

    onRoster() {
      if (!net) return;
      // A seat whose peer is gone stops being able to act; the ship simply gets harder, which is the
      // right degradation for a co-op run and never a frozen board.
      for (const [id, seat] of [...seatOf]) {
        if (!net.peers().includes(id) && id !== net.selfId) {
          seatOf.delete(id);
          cfg.ids[seat] = null;
        }
      }
    },

    isHost: () => host,
    pending: () => [...unconfirmed],

    destroy() {
      destroyed = true;
      ended = true;
      (sendSnap as unknown as { off?: () => void })?.off?.();
      (sendAct as unknown as { off?: () => void })?.off?.();
      (sendEv as unknown as { off?: () => void })?.off?.();
    },
  };

  function broadcast(): void {
    if (!net || !host) return;
    sendSnap?.(buildSnap());
  }
}

/** Build the Game a round starts with, from the frozen roster and the host's gossiped mode. */
export function gameForRound(seed: number, modeId: string, players: number): Game {
  return new Game({ seed, mode: modeOf(modeId), seats: Math.max(1, players) });
}
